import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Building2, 
  Phone, 
  ExternalLink, 
  Shield, 
  FileText,
  Loader2,
  X,
  AlertCircle,
  Info
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { displayCompanyName } from "@/lib/displayCompanyName";

interface CompanyData {
  id: string;
  name: string;
  legal_name?: string | null;
  dba_name?: string | null;
  company_type: string;
  mc_number?: string | null;
  dot_number?: string | null;
  email?: string | null;
  phone?: string | null;
  fax?: string | null;
  website?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  mailing_address_line1?: string | null;
  mailing_address_line2?: string | null;
  mailing_city?: string | null;
  mailing_state?: string | null;
  mailing_postal_code?: string | null;
  mailing_country?: string | null;
  mailing_phone?: string | null;
  mailing_fax?: string | null;
  verification_status?: string | null;
  documents_verified_at?: string | null;
  operation_type?: string | null;
  is_active?: boolean | null;
  out_of_interstate_services?: boolean | null;
  commodities?: string | null;
  special_commodities?: string | null;
  memberships?: string | null;
  authority_expiry_date?: string | null;
  insurance_expiry_date?: string | null;
  common_authority_status?: string | null;
  contract_authority_status?: string | null;
  broker_authority_status?: string | null;
  common_authority_pending?: boolean | null;
  contract_authority_pending?: boolean | null;
  broker_authority_pending?: boolean | null;
  freight_authorized?: boolean | null;
  passenger_authorized?: boolean | null;
  hhg_authorized?: boolean | null;
  bipd_required?: number | null;
  bipd_on_file?: number | null;
  cargo_required?: boolean | null;
  cargo_on_file?: boolean | null;
  bond_required?: boolean | null;
  bond_on_file?: boolean | null;
  insurance_policy_number?: string | null;
  insurance_coverage_from?: number | null;
  insurance_coverage_to?: number | null;
  insurance_carrier_name?: string | null;
  insurance_contact?: string | null;
  insurance_carrier_address?: string | null;
  insurance_carrier_phone?: string | null;
  insurance_carrier_fax?: string | null;
  insurance_effective_date?: string | null;
  insurance_cancellation_date?: string | null;
}

interface CompanyDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId?: string | null;
  companyName?: string;
}

const InfoField = ({ label, value, isLink, href, external }: { 
  label: string; 
  value: string | null | undefined; 
  isLink?: boolean;
  href?: string;
  external?: boolean;
}) => (
  <div className="min-w-0">
    <p className="text-xs text-muted-foreground mb-0.5">{label}:</p>
    {isLink && value && href ? (
      <a 
        href={href} 
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className="text-sm font-medium text-lr-cyan hover:underline flex items-center gap-1"
      >
        {value}
        {external && <ExternalLink className="w-3 h-3" />}
      </a>
    ) : (
      <p className="text-sm font-medium text-foreground">{value || '—'}</p>
    )}
  </div>
);

const SectionCard = ({ title, sourceLabel, sourceHref, children }: {
  title: string;
  sourceLabel?: string;
  sourceHref?: string;
  children: React.ReactNode;
}) => (
  <div className="bg-card border border-border rounded-lg">
    <div className="flex items-center justify-between p-4 border-b border-border">
      <h3 className="font-semibold text-foreground">{title}</h3>
      {sourceLabel && (
        <p className="text-xs text-muted-foreground">
          Source: {sourceHref ? (
            <a href={sourceHref} target="_blank" rel="noopener noreferrer" className="text-lr-cyan hover:underline">
              {sourceLabel} <ExternalLink className="w-3 h-3 inline" />
            </a>
          ) : sourceLabel}
        </p>
      )}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const CompanyDetailsModal: React.FC<CompanyDetailsModalProps> = ({
  isOpen,
  onClose,
  companyId,
  companyName
}) => {
  const [company, setCompany] = useState<CompanyData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && companyId) {
      fetchCompanyDetails();
    } else if (isOpen && !companyId) {
      setCompany(null);
    }
  }, [isOpen, companyId]);

  const fetchCompanyDetails = async () => {
    if (!companyId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .maybeSingle();

      if (error) throw error;
      setCompany(data ?? null);
    } catch (error) {
      console.error('Error fetching company details:', error);
      setCompany(null);
    } finally {
      setLoading(false);
    }
  };

  const formatBusinessAddress = () => {
    if (!company) return null;
    const parts = [
      company.address_line1,
      company.address_line2,
      company.city && company.state 
        ? `${company.city}, ${company.state} ${company.postal_code || ''}`.trim()
        : company.city || company.state,
      company.country || 'US'
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ').toUpperCase() : null;
  };

  const formatMailingAddress = () => {
    if (!company) return null;
    const parts = [
      company.mailing_address_line1,
      company.mailing_address_line2,
      company.mailing_city && company.mailing_state 
        ? `${company.mailing_city}, ${company.mailing_state} ${company.mailing_postal_code || ''}`.trim()
        : company.mailing_city || company.mailing_state,
      company.mailing_country || 'US'
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ').toUpperCase() : null;
  };

  const getVerificationBadge = (documentsVerifiedAt?: string | null) => {
    if (!documentsVerifiedAt) return null;
    return (
      <Badge className="bg-success-subtle text-success border-success/30">
        <Shield className="w-3 h-3 mr-1" />
        Verified
      </Badge>
    );
  };

  const getOperatingStatus = () => {
    if (!company) return '—';
    const type = company.company_type?.toLowerCase();
    if (type === 'broker') return 'AUTHORIZED FOR BROKER Property';
    if (type === 'carrier') return 'AUTHORIZED FOR CARRIER Property';
    if (type === 'shipper') return 'SHIPPER';
    return company.company_type?.toUpperCase() || '—';
  };

  const capitalize = (s?: string | null) => {
    if (!s) return null;
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const displayName = displayCompanyName(company?.name) || displayCompanyName(companyName) || 'Unknown Company';

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50">
      <div className="bg-background border-t border-border rounded-t-xl shadow-xl w-full h-[90vh] md:h-[85vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-3 md:p-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-0">
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                <h2 className="text-base md:text-lg font-bold text-foreground">Company Details</h2>
                {getVerificationBadge(company?.documents_verified_at)}
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content - Single Column */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4 max-w-4xl mx-auto">
              {/* Section A: Company Identity */}
              <SectionCard title="Company Identity">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <InfoField label="Company name" value={company?.name} />
                  <InfoField label="Legal / Authority name" value={company?.legal_name?.toUpperCase()} />
                  <InfoField label="DBA name" value={company?.dba_name} />
                  <InfoField label="Entity type" value={company?.company_type?.toUpperCase()} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <InfoField 
                    label="MC / Docket" 
                    value={company?.mc_number ? `MC${company.mc_number}` : null}
                    isLink={!!company?.mc_number}
                    href={`https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=MC_MX&query_string=${company?.mc_number}`}
                    external
                  />
                  <InfoField 
                    label="DOT Number" 
                    value={company?.dot_number}
                    isLink={!!company?.dot_number}
                    href={`https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=USDOT&query_string=${company?.dot_number}`}
                    external
                  />
                  <InfoField label="Company type" value={capitalize(company?.company_type)} />
                  <InfoField label="Memberships / Affiliations" value={company?.memberships} />
                </div>
              </SectionCard>

              {/* Section B: FMCSA Registration */}
              <SectionCard title="FMCSA Registration" sourceLabel="FMCSA" sourceHref="https://safer.fmcsa.dot.gov">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <InfoField label="Operating status" value={getOperatingStatus()} />
                  <InfoField label="Operation type" value={capitalize(company?.operation_type) || '—'} />
                  <InfoField label="Active" value={company?.is_active == null ? '—' : company.is_active ? 'Yes' : 'No'} />
                  <InfoField label="Out of interstate services" value={company?.out_of_interstate_services == null ? '—' : company.out_of_interstate_services ? 'Yes' : 'No'} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <InfoField label="Business address" value={formatBusinessAddress()} />
                  <InfoField 
                    label="Business phone" 
                    value={company?.phone}
                    isLink={!!company?.phone}
                    href={`tel:${company?.phone}`}
                    external
                  />
                  <InfoField label="Business fax" value={company?.fax} />
                  <InfoField label="Email" value={company?.email} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <InfoField label="Mailing address" value={formatMailingAddress() || formatBusinessAddress()} />
                  <InfoField 
                    label="Mailing phone" 
                    value={company?.mailing_phone}
                    isLink={!!company?.mailing_phone}
                    href={`tel:${company?.mailing_phone}`}
                    external
                  />
                  <InfoField label="Mailing fax" value={company?.mailing_fax} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <InfoField label="Commodities" value={company?.commodities} />
                  <InfoField label="Special commodities" value={company?.special_commodities} />
                </div>
              </SectionCard>

              {/* DOT Authority & Insurance */}
              <SectionCard 
                title={`DOT Authority & Insurance ${company?.authority_expiry_date ? `as of ${new Date(company.authority_expiry_date).toLocaleDateString()}` : ''}`}
                sourceLabel="FMCSA" 
                sourceHref="https://safer.fmcsa.dot.gov"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  {/* DOT Authority Status */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-3">DOT Authority Status</p>
                    <div className="border border-border rounded overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-2 font-medium text-muted-foreground">Authority:</th>
                            <th className="text-left p-2 font-medium text-muted-foreground">Status:</th>
                            <th className="text-left p-2 font-medium text-muted-foreground">Application pending:</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-t border-border">
                            <td className="p-2">Common</td>
                            <td className="p-2">{capitalize(company?.common_authority_status) || 'None'}</td>
                            <td className="p-2">{company?.common_authority_pending ? 'Yes' : 'No'}</td>
                          </tr>
                          <tr className="border-t border-border">
                            <td className="p-2">Contract</td>
                            <td className="p-2">{capitalize(company?.contract_authority_status) || 'None'}</td>
                            <td className="p-2">{company?.contract_authority_pending ? 'Yes' : 'No'}</td>
                          </tr>
                          <tr className="border-t border-border">
                            <td className="p-2">Broker</td>
                            <td className="p-2">{capitalize(company?.broker_authority_status) || (company?.company_type === 'broker' ? 'Active' : 'None')}</td>
                            <td className="p-2">{company?.broker_authority_pending ? 'Yes' : 'No'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <InfoField label="Freight" value={company?.freight_authorized ? 'Yes' : 'No'} />
                      <InfoField label="Passenger" value={company?.passenger_authorized ? 'Yes' : 'No'} />
                      <InfoField label="HHG" value={company?.hhg_authorized ? 'Yes' : 'No'} />
                    </div>
                  </div>

                  {/* DOT Insurance Requirements */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-3">DOT Insurance Requirements</p>
                    <div className="border border-border rounded overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-2 font-medium text-muted-foreground">Ins. Type:</th>
                            <th className="text-right p-2 font-medium text-muted-foreground">Required:</th>
                            <th className="text-right p-2 font-medium text-muted-foreground">On File:</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-t border-border">
                            <td className="p-2">BIPD</td>
                            <td className="p-2 text-right">${company?.bipd_required?.toLocaleString() || '0'}</td>
                            <td className="p-2 text-right">${company?.bipd_on_file?.toLocaleString() || '0'}</td>
                          </tr>
                          <tr className="border-t border-border">
                            <td className="p-2">Cargo</td>
                            <td className="p-2 text-right">{company?.cargo_required ? 'Yes' : 'No'}</td>
                            <td className="p-2 text-right">{company?.cargo_on_file ? 'Yes' : 'No'}</td>
                          </tr>
                          <tr className="border-t border-border">
                            <td className="p-2">Bond</td>
                            <td className="p-2 text-right">{company?.bond_required ? 'Yes' : 'No'}</td>
                            <td className="p-2 text-right">{company?.bond_on_file ? 'Yes' : 'No'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Insurance Details */}
                <div className="mt-6 pt-4 border-t border-border">
                  <p className="text-sm font-medium text-muted-foreground mb-3">DOT Active / Pending Insurance</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <InfoField label="Policy / surety" value={company?.insurance_policy_number} />
                    <InfoField label="Coverage from" value={`$${company?.insurance_coverage_from?.toLocaleString() || '0'}`} />
                    <InfoField label="Coverage to" value={`$${company?.insurance_coverage_to?.toLocaleString() || '0'}`} />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <InfoField label="Insurance carrier" value={company?.insurance_carrier_name} />
                    <InfoField label="Contact" value={company?.insurance_contact} />
                    <InfoField label="Address" value={company?.insurance_carrier_address} />
                    <InfoField 
                      label="Phone" 
                      value={company?.insurance_carrier_phone}
                      isLink={!!company?.insurance_carrier_phone}
                      href={`tel:${company?.insurance_carrier_phone}`}
                      external
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <InfoField label="Fax" value={company?.insurance_carrier_fax} />
                    <InfoField 
                      label="Effective date" 
                      value={company?.insurance_effective_date 
                        ? new Date(company.insurance_effective_date).toLocaleDateString() 
                        : null
                      } 
                    />
                    <InfoField 
                      label="Cancellation date" 
                      value={company?.insurance_cancellation_date 
                        ? new Date(company.insurance_cancellation_date).toLocaleDateString() 
                        : null
                      } 
                    />
                  </div>
                </div>
              </SectionCard>

              {/* Credit Intelligence Placeholder */}
              <div className="bg-muted/30 border border-border rounded-lg p-4 flex items-start gap-3">
                <Info className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Credit Intelligence</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Credit intelligence is coming soon. Payment history, credit scores, and days-to-pay insights will appear here in a future update.
                  </p>
                </div>
              </div>

              {/* No data warning for missing companies */}
              {!company && !loading && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-500">Limited Information</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Detailed company information is not available. This may be an external load source or the company has
                      not been verified yet.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CompanyDetailsModal;
